import React, { useState, useEffect } from 'react';
import { X, Eye, EyeOff, CheckCircle, AlertCircle } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import Button from '../ui/button';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';

interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function AuthModal({ isOpen, onClose }: AuthModalProps) {
  const navigate = useNavigate();
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [userType, setUserType] = useState<'client' | 'candidat'>('candidat');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);

  if (!isOpen) return null;

  const validateEmail = (email: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

  const handleAuthError = (error: any) => {
    console.error('Auth error:', error);
    
    if (error.message?.includes('Database error saving new user')) {
      return 'Erreur lors de la création du compte. Veuillez réessayer.';
    }
    
    if (error.message?.includes('Database connection error') || error.message?.includes('database error')) {
      return 'Erreur de connexion à la base de données. Veuillez réessayer dans quelques instants.';
    }

    if (error.message?.toLowerCase().includes('invalid login credentials') || error?.code === 'invalid_credentials') {
      return 'Email ou mot de passe incorrect. Veuillez vérifier vos identifiants.';
    }

    if (error.message?.toLowerCase().includes('already registered') || error?.code === 'user_already_exists') {
      return 'Un compte existe déjà avec cet email. Veuillez vous connecter.';
    }

    if (error.message?.toLowerCase().includes('rate limit')) {
      return 'Trop de tentatives. Veuillez réessayer dans quelques minutes.';
    }

    if (error.message?.toLowerCase().includes('network')) {
      return 'Erreur de connexion au serveur. Veuillez vérifier votre connexion internet.';
    }

    if (error.message?.includes('violates unique constraint') || error.message?.includes('duplicate key')) {
      return 'Cette adresse email est déjà utilisée. Veuillez en choisir une autre.';
    }

    return error.message || 'Une erreur est survenue. Veuillez réessayer.';
  };

  const showWelcomeToast = (name: string) => {
    toast.custom((t) => (
      <div className={`${t.visible ? 'animate-enter' : 'animate-leave'} max-w-md w-full bg-white shadow-lg rounded-lg pointer-events-auto flex ring-1 ring-black ring-opacity-5`}>
        <div className="flex-1 w-0 p-4">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <CheckCircle className="h-10 w-10 text-primary" />
            </div>
            <div className="ml-3 flex-1">
              <p className="text-sm font-medium text-gray-900">Bienvenue sur recruteurs.io</p>
              <p className="mt-1 text-sm text-gray-500">Ravi de vous revoir, {name} !</p>
            </div>
          </div>
        </div>
        <div className="flex border-l border-gray-200">
          <button onClick={() => toast.dismiss(t.id)} className="w-full p-4 text-sm font-medium text-primary hover:text-primary-600">
            Fermer
          </button>
        </div>
      </div>
    ), { duration: 4000 });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!validateEmail(email)) {
      setError('Veuillez entrer une adresse email valide');
      return;
    }

    if (password.length < 6) {
      setError('Le mot de passe doit contenir au moins 6 caractères');
      return;
    }

    setLoading(true);

    try {
      if (isLogin) {
        const { data: signInData, error: signInError } = await supabase.auth.signInWithPassword({
          email: email.trim(),
          password,
        });

        if (signInError) throw signInError;

        if (!signInData.user) throw new Error('No user data returned');

        const { data: userData, error: userError } = await supabase
          .from('users')
          .select('first_name')
          .eq('id', signInData.user.id)
          .single();

        if (userError) throw userError;

        if (userData) {
          showWelcomeToast(userData.first_name);
          onClose();
          navigate('/');
        }
      } else {
        if (!firstName.trim() || !lastName.trim()) {
          throw new Error('Veuillez remplir tous les champs');
        }

        const { data: signUpData, error: signUpError } = await supabase.auth.signUp({
          email: email.trim(),
          password,
          options: {
            data: {
              first_name: firstName.trim(),
              last_name: lastName.trim(),
              user_type: userType,
            }
          }
        });

        if (signUpError) throw signUpError;
        if (!signUpData.user) throw new Error('No user data returned');

        // Wait for auth to settle
        await new Promise(resolve => setTimeout(resolve, 1000));

        const { error: insertError } = await supabase
          .from('users')
          .upsert([
            {
              id: signUpData.user.id,
              email: email.trim(),
              first_name: firstName.trim(),
              last_name: lastName.trim(),
              role: userType,
              user_type: userType,
            }
          ], {
            onConflict: 'id'
          });

        if (insertError) {
          console.error('Profile creation error:', insertError);
          throw new Error('Erreur lors de la création du profil. Veuillez réessayer.');
        }

        showWelcomeToast(firstName.trim());
        onClose();
        navigate('/');
      }
    } catch (err: any) {
      console.error('Auth operation error:', err);
      const errorMessage = handleAuthError(err);
      setError(errorMessage);
      
      if (err?.code === 'user_already_exists') {
        setIsLogin(true);
      }
      
      toast.error(errorMessage, {
        icon: <AlertCircle className="h-5 w-5 text-red-500" />,
        duration: 4000,
        position: 'top-center'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black bg-opacity-50" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-strong p-8 max-w-md w-full mx-4">
        <button onClick={onClose} className="absolute top-4 right-4 text-neutral-400 hover:text-neutral-600">
          <X size={24} />
        </button>

        <div className="h-10 overflow-hidden text-2xl font-bold text-center text-transparent bg-clip-text bg-gradient-to-r from-[#d03484] to-[#d47254]">
          <div className="flex flex-col animate-slideWords leading-snug">
            <span>Bienvenue</span>
          </div>
        </div>

        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-100 text-red-600 rounded-lg text-sm flex items-start">
            <AlertCircle className="h-5 w-5 mr-2 flex-shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          {!isLogin && (
            <>
              <div>
                <label htmlFor="firstName" className="block text-sm font-medium text-neutral-700 mb-1">
                  Prénom
                </label>
                <input
                  type="text"
                  id="firstName"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  className="w-full px-4 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  required
                />
              </div>
              <div>
                <label htmlFor="lastName" className="block text-sm font-medium text-neutral-700 mb-1">
                  Nom
                </label>
                <input
                  type="text"
                  id="lastName"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  className="w-full px-4 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  required
                />
              </div>
              <div>
                <label htmlFor="userType" className="block text-sm font-medium text-neutral-700 mb-1">
                  Vous êtes
                </label>
                <select
                  value={userType}
                  onChange={(e) => setUserType(e.target.value as 'client' | 'candidat')}
                  className="w-full border px-3 py-2 rounded-md text-sm text-neutral-800"
                >
                  <option value="client">Entreprise</option>
                  <option value="candidat">Candidat</option>
                </select>
              </div>
            </>
          )}
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-neutral-700 mb-1">
              Email
            </label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-4 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              required
              autoComplete="email"
            />
          </div>
          <div>
            <label htmlFor="password" className="block text-sm font-medium text-neutral-700 mb-1">
              Mot de passe
            </label>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent pr-12"
                required
                autoComplete={isLogin ? "current-password" : "new-password"}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-600"
              >
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
            {!isLogin && (
              <p className="mt-1 text-sm text-neutral-500">
                Minimum 6 caractères
              </p>
            )}
          </div>

          <Button
            type="submit"
            variant="gradient"
            fullWidth
            disabled={loading}
          >
            {loading ? 'Chargement...' : isLogin ? 'Se connecter' : 'Créer un compte'}
          </Button>
        </form>

        <div className="mt-4 text-center">
          <button
            onClick={() => {
              setIsLogin(!isLogin);
              setError(null);
              setEmail('');
              setPassword('');
              setFirstName('');
              setLastName('');
              setShowPassword(false);
            }}
            className="text-primary hover:text-primary-600"
          >
            {isLogin ? "Pas encore de compte ? S'inscrire" : 'Déjà un compte ? Se connecter'}
          </button>
        </div>
      </div>
    </div>
  );
}